/**
 * useLearningPaths — fetch the current agent's assigned learning paths.
 *
 * EPIC-08-09: GET /api/v1/learning-paths
 */

import { useState, useEffect } from 'react';
import apiClient from '../api/client';

export interface CurrentCourse {
  title: string;
  type: string;
  status: string;
}

export interface LearningPath {
  id: string;
  title: string;
  status: 'not_started' | 'in_progress' | 'completed';
  current_course_sequence: number;
  total_courses: number;
  current_course: CurrentCourse | null;
}

export function useLearningPaths(): {
  paths: LearningPath[];
  isLoading: boolean;
  error: string | null;
} {
  const [paths, setPaths] = useState<LearningPath[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    apiClient
      .get<{ data: { learning_paths: LearningPath[] } }>('/learning-paths')
      .then((res) => {
        if (!cancelled) setPaths(res.data.data.learning_paths);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load paths');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { paths, isLoading, error };
}
